/**
 * AI analysis of screen data – mirrors React Native AIService.
 * Uses Chillverify AI middleware (Vercel AI Gateway) when configured; falls back to local analysis.
 */

import { getBatteryPercentage } from '../lib/batteryUtils';
import { analyzeDevicesWithContext, analyzeGatewaysWithContext, buildGatewayIntelligenceInstruction, buildIntelligenceInstruction } from '../utils/intelligenceContext';
import { supabase } from './supabaseClient';

// Page-specific instructions (same as React Native AIService PAGE_INSTRUCTIONS)
const PAGE_INSTRUCTIONS = {
  devices: 'Analyze sensor devices using any intelligenceContext on each device (use case, expected temp range, notes). Explain what readings mean for that context and flag compliance or spoilage risk.',
  shop: 'Analyze the current shop products, their features, pricing, categories, and provide insights about the product catalog, popular items, and recommendations for customers.',
  zones: 'Analyze the current zones, their sensor configurations, sensor counts, zone types, and provide insights about zone organization, sensor distribution, and monitoring coverage.',
  DeviceDetail: "Analyze this specific sensor's performance and identify any issues.",
  TripMap: 'Analyze the trip data and provide insights about environmental changes during the journey.',
  Settings: 'Analyze the current system configuration and provide optimization recommendations.',
  ZoneReports: "Analyze the zone report data including trip statistics, temperature trends, humidity levels, GPS data, and provide insights about the zone's environmental conditions, performance patterns, and any anomalies or recommendations for optimization.",
  gateways: 'Analyze gateways and their connectedSensors. Use intelligenceContext on each sensor to explain compliance and spoilage risk for monitored use cases.',
};

function getPageInstruction(page) {
  return PAGE_INSTRUCTIONS[page] || 'Analyze the current screen data and provide insights';
}

/**
 * Convert battery from mV to percentage (same logic as React Native AIService).
 * So the AI receives actual percentage, not raw mV.
 */
function mvToBatteryPercentage(batteryLevel, deviceType) {
  if (batteryLevel === undefined || batteryLevel === null || batteryLevel <= 0 || Number.isNaN(batteryLevel)) {
    return null;
  }
  if (batteryLevel >= 0 && batteryLevel <= 100) {
    return Math.min(100, Math.max(0, Math.round(batteryLevel)));
  }
  const pct = getBatteryPercentage(batteryLevel, deviceType);
  return pct;
}

/**
 * Extract and prepare screen data for the API (same as RN extractScreenData).
 * Converts battery to percentage so the AI receives consistent data.
 */
export function extractScreenData(screenData, context) {
  if (!Array.isArray(screenData)) return [];
  if (context === 'gateways') {
    return screenData.map((item) => ({ ...item }));
  }
  return screenData.map((item) => {
    const lastReading = item.lastReading || item.latest_reading;
    const deviceType = item.device_type ?? item.deviceType ?? item.type;
    const rawBattery = lastReading?.battery_level ?? lastReading?.batteryLevel ?? item.battery ?? item.batteryLevel;
    const batteryPct = mvToBatteryPercentage(rawBattery, deviceType);
    const readingForAi = lastReading
      ? {
          ...lastReading,
          batteryLevel: batteryPct !== null ? batteryPct : lastReading.battery_level ?? lastReading.batteryLevel,
        }
      : lastReading;
    return {
      ...item,
      lastReading: readingForAi,
    };
  });
}

/**
 * Normalize API response (same as React Native normalizeAnalysisResponse).
 * Handles string, object, or JSON-in-string and returns { analysis, insights, recommendations, cached, confidence }.
 */
function normalizeAnalysisResponse(result) {
  let data = result;
  if (typeof result === 'string') {
    try {
      data = JSON.parse(result);
    } catch {
      data = { analysis: result };
    }
  }

  let analysis = data?.analysis;
  let insights = Array.isArray(data?.insights) ? data.insights : [];
  let recommendations = Array.isArray(data?.recommendations) ? data.recommendations : [];

  if (analysis != null && typeof analysis === 'object') {
    const text = analysis.analysis ?? analysis.summary ?? analysis.text ?? analysis.message ?? analysis.content;
    analysis = typeof text === 'string' ? text : '';
  }

  if (typeof analysis === 'string') {
    const trimmed = analysis.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(analysis);
        if (typeof parsed.analysis === 'string') analysis = parsed.analysis;
        else if (typeof parsed.summary === 'string') analysis = parsed.summary;
        else if (typeof parsed.text === 'string') analysis = parsed.text;
        if (Array.isArray(parsed.insights)) insights = parsed.insights.map(String);
        if (Array.isArray(parsed.recommendations)) recommendations = parsed.recommendations.map(String);
      } catch {
        const raw = analysis;
        const analysisMatch = raw.match(/"analysis"\s*:\s*"((?:[^"\\]|\\.)*)"/);
        if (analysisMatch?.[1]) {
          analysis = analysisMatch[1].replace(/\\"/g, '"');
        }
        const insightsMatch = raw.match(/"insights"\s*:\s*\[([\s\S]*?)\]/);
        if (insightsMatch?.[1]) {
          const quoted = insightsMatch[1].match(/"((?:[^"\\]|\\.)*)"/g);
          if (quoted) insights = quoted.map((s) => s.slice(1, -1).replace(/\\"/g, '"'));
        }
        const recMatch = raw.match(/"recommendations"\s*:\s*\[([\s\S]*?)\]/);
        if (recMatch?.[1]) {
          const quoted = recMatch[1].match(/"((?:[^"\\]|\\.)*)"/g);
          if (quoted) recommendations = quoted.map((s) => s.slice(1, -1).replace(/\\"/g, '"'));
        }
      }
    }
  }

  let outAnalysis = typeof analysis === 'string' ? analysis : String(analysis ?? '');
  outAnalysis = outAnalysis.replace(/\\_/g, '_');

  const confidence =
    typeof (data?.confidence ?? result?.confidence) === 'number' ? data?.confidence ?? result?.confidence : 1;

  return {
    analysis: outAnalysis,
    insights: insights.map((s) => String(s).replace(/\\_/g, '_')),
    recommendations: recommendations.map((s) => String(s).replace(/\\_/g, '_')),
    cached: Boolean(data?.cached ?? result?.cached),
    confidence,
    contextsUsed: Array.isArray(data?.contextsUsed) ? data.contextsUsed : [],
    devicesWithoutContext: Array.isArray(data?.devicesWithoutContext) ? data.devicesWithoutContext : [],
  };
}

/**
 * Call Vercel AI Gateway via the Netlify function so the gateway key stays server-side.
 * Optional Cloud Run URL is only used if the function is unavailable (legacy).
 */
async function callMiddleware(data, page, instruction) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  const body = JSON.stringify({
    data,
    page,
    instruction: instruction || getPageInstruction(page),
  });

  const response = await fetch('/.netlify/functions/ai-analyze', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI analysis failed: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  return normalizeAnalysisResponse(result);
}

/**
 * Analyze screen data: use middleware when available, otherwise local analysis (same shape as RN).
 * @param {Array<Object>} data - List of devices or gateways (same shape as list pages).
 * @param {'devices'|'gateways'} context - Page context for instruction and fallback.
 * @param {string} [instruction] - Optional custom instruction (like RN).
 * @returns {Promise<{ analysis: string, insights: string[], recommendations: string[], cached?: boolean, confidence?: number }>}
 */
export async function analyzeScreenData(data, context, instruction) {
  if (!Array.isArray(data)) {
    return {
      analysis: 'No data available to analyze.',
      confidence: 0,
      insights: [],
      recommendations: [],
      cached: false,
    };
  }

  const preparedData = extractScreenData(data, context);
  const page = context === 'gateways' ? 'gateways' : 'devices';
  const hasIntelligenceContext = preparedData.some(
    (d) => d?.hasIntelligenceContext || d?.intelligenceContext || d?.connectedSensors?.some?.((s) => s?.intelligenceContext),
  );
  const resolvedInstruction =
    instruction ||
    (context === 'gateways' && hasIntelligenceContext
      ? buildGatewayIntelligenceInstruction(preparedData)
      : hasIntelligenceContext
        ? buildIntelligenceInstruction(preparedData)
        : getPageInstruction(page));

  try {
    console.log('🤖 AI Service: Analyzing screen data...', {
      page,
      dataPoints: preparedData.length,
      withContext: preparedData.filter((d) => d?.hasIntelligenceContext).length,
    });
    const result = await callMiddleware(preparedData, page, resolvedInstruction);
    console.log('🤖 AI Service: Analysis completed', { cached: result.cached, insightsCount: result.insights?.length });
    if (!result.contextsUsed?.length && hasIntelligenceContext) {
      if (context === 'gateways') {
        result.contextsUsed = preparedData.flatMap((g) =>
          (g.connectedSensors || [])
            .filter((s) => s.intelligenceContext)
            .map((s) => ({
              gatewayName: g.name,
              deviceName: s.name,
              contextLabel: s.intelligenceContext.label,
              contextIcon: s.intelligenceContext.icon,
              tempRange: s.intelligenceContext.tempRange,
              critical: s.intelligenceContext.critical,
            })),
        );
      } else {
        result.contextsUsed = preparedData
          .filter((d) => d.intelligenceContext)
          .map((d) => ({
            deviceName: d.name,
            contextLabel: d.intelligenceContext.label,
            contextIcon: d.intelligenceContext.icon,
            tempRange: d.intelligenceContext.tempRange,
            critical: d.intelligenceContext.critical,
          }));
      }
    }
    return result;
  } catch (err) {
    console.warn('⚠️ AI middleware failed, using local analysis:', err?.message || err);
  }

  // Fallback: local analysis (same structure as RN response)
  if (context === 'devices') {
    if (hasIntelligenceContext) {
      const local = analyzeDevicesWithContext(preparedData);
      return { ...local, cached: false };
    }
    const local = analyzeDevicesLocal(data);
    return { ...local, cached: false, contextsUsed: [], devicesWithoutContext: [] };
  }
  if (context === 'gateways') {
    if (hasIntelligenceContext) {
      const local = analyzeGatewaysWithContext(preparedData);
      return { ...local, cached: false };
    }
    const local = analyzeGatewaysLocal(data);
    return { ...local, cached: false, contextsUsed: [], devicesWithoutContext: [] };
  }

  return {
    analysis: 'Unknown context. Analysis is available for devices or gateways.',
    confidence: 0,
    insights: [],
    recommendations: [],
    cached: false,
  };
}

function analyzeDevicesLocal(devices) {
  const n = devices.length;
  const statusCounts = { online: 0, warning: 0, offline: 0, unknown: 0 };
  const lowBattery = [];
  const tempWarnings = [];
  const humidityWarnings = [];
  const byType = {};

  devices.forEach((d) => {
    const status = (d.status || 'unknown').toLowerCase();
    statusCounts[status] = (statusCounts[status] ?? 0) + 1;
    const type = d.deviceType || d.device_type || 'Unknown';
    byType[type] = (byType[type] || 0) + 1;
    const bat = d.battery ?? d.batteryLevel;
    if (typeof bat === 'number' && bat < 20) lowBattery.push(d.name || d.id);
    if (d.temperatureStatus === 'warning' || d.temperatureStatus === 'critical') tempWarnings.push(d.name || d.id);
    if (d.humidityStatus === 'warning' || d.humidityStatus === 'critical') humidityWarnings.push(d.name || d.id);
  });

  const typeLines = Object.entries(byType)
    .map(([type, count]) => `${type}: ${count}`)
    .join('; ');
  let analysis = `You have ${n} device${n !== 1 ? 's' : ''} (${typeLines}). `;
  analysis += `Status: ${statusCounts.online || 0} online, ${statusCounts.warning || 0} warning, ${statusCounts.offline || 0} offline. `;
  if (lowBattery.length)
    analysis += `Low battery (under 20%): ${lowBattery.slice(0, 5).join(', ')}${lowBattery.length > 5 ? ' and others.' : '.'} `;
  if (tempWarnings.length)
    analysis += `Temperature alerts: ${tempWarnings.slice(0, 3).join(', ')}${tempWarnings.length > 3 ? ' and others.' : '.'} `;
  if (humidityWarnings.length)
    analysis += `Humidity alerts: ${humidityWarnings.slice(0, 3).join(', ')}${humidityWarnings.length > 3 ? ' and others.' : '.'}`;

  const insights = [];
  if (statusCounts.offline > 0)
    insights.push(`${statusCounts.offline} device(s) are offline — check connectivity or battery.`);
  if (lowBattery.length)
    insights.push(`${lowBattery.length} device(s) have low battery — consider replacing or recharging.`);
  if (tempWarnings.length)
    insights.push(`Temperature outside range on ${tempWarnings.length} device(s) — verify storage conditions.`);
  if (humidityWarnings.length)
    insights.push(`Humidity outside range on ${humidityWarnings.length} device(s) — check environment.`);
  if (statusCounts.online === n && n > 0) insights.push('All devices are currently online and reporting.');

  const recommendations = [];
  if (statusCounts.offline > 0)
    recommendations.push('Review offline devices: ensure they are in range and powered.');
  if (lowBattery.length)
    recommendations.push('Plan battery replacement or recharge for low-battery devices.');
  if (tempWarnings.length || humidityWarnings.length)
    recommendations.push('Address environmental alerts to stay within compliance ranges.');
  if (insights.length === 0 && n > 0)
    recommendations.push('Fleet looks healthy. Keep monitoring for trends over time.');

  return {
    analysis: analysis.trim(),
    confidence: 0.85,
    insights: insights.length ? insights : ['No notable issues detected from the current snapshot.'],
    recommendations: recommendations.length ? recommendations : ['Continue regular monitoring.'],
  };
}

function analyzeGatewaysLocal(gateways) {
  const n = gateways.length;
  const statusCounts = { online: 0, offline: 0, warning: 0, unknown: 0 };
  const byModel = {};

  gateways.forEach((g) => {
    const status = (g.status || g.connectivity || 'unknown').toLowerCase();
    statusCounts[status] = (statusCounts[status] ?? 0) + 1;
    const model = g.model || g.name || 'Gateway';
    byModel[model] = (byModel[model] || 0) + 1;
  });

  const modelLines = Object.entries(byModel)
    .map(([m, c]) => `${m}: ${c}`)
    .join('; ');
  let analysis = `You have ${n} gateway${n !== 1 ? 's' : ''} (${modelLines}). `;
  analysis += `Connectivity: ${statusCounts.online || 0} online, ${statusCounts.offline || 0} offline, ${statusCounts.warning || 0} with warnings.`;

  const insights = [];
  if (statusCounts.offline > 0)
    insights.push(`${statusCounts.offline} gateway(s) are offline — check power and network.`);
  if (statusCounts.warning > 0)
    insights.push(`${statusCounts.warning} gateway(s) have warnings — review device or connectivity issues.`);
  if (statusCounts.online === n && n > 0) insights.push('All gateways are currently online.');

  const recommendations = [];
  if (statusCounts.offline > 0)
    recommendations.push('Troubleshoot offline gateways: power, Ethernet/Wi‑Fi, and firewall rules.');
  if (statusCounts.warning > 0)
    recommendations.push('Check gateway logs and connected sensors for the source of warnings.');
  if (!recommendations.length && n > 0)
    recommendations.push('Gateway fleet is healthy. Monitor for connectivity trends.');

  return {
    analysis: analysis.trim(),
    confidence: 0.85,
    insights: insights.length ? insights : ['No notable gateway issues from the current data.'],
    recommendations: recommendations.length ? recommendations : ['Continue monitoring gateway connectivity.'],
  };
}
